import { Component, ElementRef, HostBinding, HostListener } from '@angular/core';
import { IdleService } from '../../services/idle/idle.service';
import { IntervalService } from '../../services/interval/interval.service';

@Component({
  selector: 'ghosthand',
  templateUrl: './ghosthand.component.html',
  styleUrls: ['./ghosthand.component.scss']
})
export class GhosthandComponent {
  @HostBinding('class.active') active = false;
  private previousElement: HTMLElement | null = null;
  private isFirstMove = true;
  private useWidgets = false;
  private interactionEvents = [
    'click', 'mousedown', 'mouseup', 'mousemove', 'keydown', 'keyup', 'scroll', 'touchstart', 'touchmove', 'touchend', 'pointerdown',
    'pointermove', 'wheel', 'focus', 'blur'
  ];

  constructor(private elRef: ElementRef, private idle: IdleService) {
    this.interactionEvents.forEach(event => { document.addEventListener(event, () => this.stop()) });
  }

  @HostListener('click')
  onHostClick() {
    this.stop();
  }

  public start() {
    this.active = true;
    this.isFirstMove = true;
    this.moveToNextItem();
    IntervalService.set('ghosthandCloseInterval', () => {
      IntervalService.clear('ghosthandCloseInterval');
      this.stop(true);
    }, 1000 * 60 * 5);
  }

  public stop(force = false) {
    if (this.active || force) {
      this.idle.restartTimeout(false);
      this.active = false;
    }
  }

  private moveToNextItem() {
    if (!this.active) return;

    const visibleItems = this.getVisibleItems();
    if (visibleItems.length === 0) {
      setTimeout(() => this.moveToNextItem(), 2000);
      return;
    }

    let item: HTMLElement;
    do {
      item = visibleItems[Math.floor(Math.random() * visibleItems.length)];
    } while (visibleItems.length > 1 && item === this.previousElement);

    this.previousElement = item;

    const rect = item.getBoundingClientRect();
    const hand = document.getElementById('ghosthand-click');
    if (!hand) return;

    const x = rect.left + rect.width / 2 - 20;
    const y = rect.top + rect.height / 2 - 20;

    if (this.isFirstMove) {
      Object.assign(hand.style, { transition: 'none', left: `${x}px`, top: `${y}px`, opacity: '1' });
      this.playClickAnimation(hand, x, y, () => {
        this.isFirstMove = false;
        this.moveToNextItem();
      });
    } else {
      Object.assign(hand.style, { transition: 'left 3s ease-in-out, top 3s ease-in-out', left: `${x + 6}px`, top: `${y}px`, opacity: '1' });
      const onTransitionEnd = () => {
        hand.removeEventListener('transitionend', onTransitionEnd); // ensure it only fires once
        this.playClickAnimation(hand, x, y, () => {
          this.moveToNextItem();
        });
      };
      hand.addEventListener('transitionend', onTransitionEnd);
    }
  }

  private playClickAnimation(hand: HTMLElement, x: number, y: number, done?: () => void) {
    hand.style.animation = 'none';
    hand.offsetHeight;
    setTimeout(() => {
      hand.style.animation = 'click 1s ease-out forwards';
      hand.classList.add('clicking');
    }, 10);

    setTimeout(() => {
      for (let i = 0; i < 10; i++) {
        const angle = (360 / 10) * i;
        const line = document.createElement('div');
        line.classList.add('ghosthand-line');
        line.style.left = `${x + 15}px`;
        line.style.top = `${y - 30}px`;
        line.style.setProperty('--angle', `${angle}deg`);
        this.elRef.nativeElement.appendChild(line);
        setTimeout(() => line.remove(), 1000);
      }
    }, 500);
    const waitAfterClickMs = 500;
    setTimeout(() => {
      hand.classList.remove('clicking');
      if (done) done();
    }, 1000 + waitAfterClickMs);
  }

  private getVisibleItems(): HTMLElement[] {
    const carousel = document.getElementById('appCarousel');
    let widgetCarousel = document.getElementById('widgetCarousel') || document.getElementById('defaultWidgetCarousel');
    if (widgetCarousel?.children?.length === 1) {
      widgetCarousel = widgetCarousel.children[0] as any;
    }
    const visibleApps = carousel ? Array.from(carousel.children).filter(el => this.isVisibleInContainer(el as HTMLElement, carousel)) as HTMLElement[] : [];
    const visibleWidgets = widgetCarousel ? Array.from(widgetCarousel.children).filter(el => {
      const icon = (el as HTMLElement).querySelector('widget-header ibex-icon') as HTMLElement;
      return icon && this.isVisibleInContainer(icon, widgetCarousel);
    }).map(el => (el as HTMLElement).querySelector('widget-header ibex-icon') as HTMLElement).filter(el => !!el) : [];
    this.useWidgets = !this.useWidgets;
    return !this.useWidgets ? visibleApps : visibleWidgets;
  }

  private isVisibleInContainer(elem: HTMLElement, container: HTMLElement): boolean {
    const elemRect = elem.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    return (elemRect.right > containerRect.left && elemRect.left < containerRect.right && elemRect.bottom > containerRect.top && elemRect.top < containerRect.bottom);
  }
}
